import { LineChart } from "@mui/x-charts/LineChart";
import { useEffect, useState } from "react";
import { useAPI } from "../../../../api/useAPI";
import { CSVEntry } from "../../../../api/types";
import { useCreateGraphFromPositions } from "./graph";

export default function AlgorithmBalanceGraph({
  algorithm,
}: {
  algorithm: string;
}) {
  const { getCsvTrackerPositions } = useAPI();
  const [positions, setPositions] = useState<CSVEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPositions = async () => {
      setLoading(true);
      try {
        const response = await getCsvTrackerPositions(algorithm);
        if (response.success) {
          setPositions(response.data);
        } else {
          console.error(
            "Failed to fetch CSV tracker positions:",
            response.error,
          );
        }
      } catch (error) {
        console.error("Error fetching CSV tracker positions:", error);
      } finally {
        setLoading(false);
      }
    };

    if (algorithm) {
      fetchPositions();
    }
  }, [algorithm, getCsvTrackerPositions]);

  const graphData = useCreateGraphFromPositions({
    positions: positions.map((pos) => ({
      ...pos,
      openTransaction: {
        filledTimestamp: pos.timestamp,
        totalEquity: 100, // Assume base portfolio value
      },
      closeTransaction:
        pos.status === "CLOSED" && pos.closeTimestamp
          ? {
              filledTimestamp: pos.closeTimestamp,
              totalEquity:
                100 *
                (1 +
                  ((pos.percentOfportfolio / 100) * (pos.pnlPercentage || 0)) /
                    100),
            }
          : undefined,
    })),
    minDistanceInMinutes: 5,
    positionEntryCallback: (dataset, position, minTs) => {
      const { openTransaction, closeTransaction } = position;

      if (openTransaction?.filledTimestamp) {
        const timestamp = new Date(openTransaction.filledTimestamp).getTime();
        minTs = Math.min(minTs, timestamp);
        dataset.push({
          x: timestamp,
          y: openTransaction.totalEquity,
        });
      }

      if (closeTransaction?.filledTimestamp && closeTransaction?.totalEquity) {
        const timestamp = new Date(closeTransaction.filledTimestamp).getTime();
        dataset.push({
          x: timestamp,
          y: closeTransaction.totalEquity,
        });
      }

      return { minTs };
    },
    lastEntryCallback: (dataset) => {
      if (positions.length > 0) {
        const lastPosition = positions[positions.length - 1];
        const lastEquity =
          lastPosition.status === "CLOSED"
            ? 100 *
              (1 +
                ((lastPosition.percentOfportfolio / 100) *
                  (lastPosition.pnlPercentage || 0)) /
                  100)
            : 100; // Assume base value if last position is open
        dataset.push({
          x: Date.now(),
          y: lastEquity,
        });
      }
    },
  });

  if (loading) {
    return <div>Loading algorithm balance data...</div>;
  }

  if (positions.length === 0) {
    return <div>No data available for algorithm: {algorithm}</div>;
  }

  return (
    <LineChart
      height={300}
      xAxis={[
        {
          dataKey: "x",
          valueFormatter: (value: number) => {
            const date = new Date(value);
            return date.toLocaleDateString("nl-NL", {
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            });
          },
          min: graphData.minTs,
          max: graphData.maxTs,
        },
      ]}
      series={[
        {
          curve: "linear",
          dataKey: "y",
          label: `Algorithm Balance (USD) - ${algorithm}`,
        },
      ]}
      dataset={graphData.dataset}
    />
  );
}
